import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import FormInput from '@enerex/unified-ui-react/form/FormInput';
import LoadingButton from '@enerex/unified-ui-react/LoadingButton';
import AddIcon from '@enerex/unified-ui-react/icons/AddIcon';
import DeleteIcon from '@enerex/unified-ui-react/icons/DeleteIcon';
import useShowSnackbar from 'hooks/useShowSnackbar';
import { getLookupRecord, readField, saveLookupRecord } from '../api';
import { appendAliasPayload, removeAliasPayload, splitAliases } from './aliasValues';

const AddAliasesModal = ({ definition, fields: recordFields, record, onClose, onSuccess, onChanged }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);
    const busy = useRef(false);
    const snackbar = useShowSnackbar();
    const { control, handleSubmit, setValue } = useForm({ defaultValues: {
        recordName: record.name || record.code || '', additions: [{ value: '' }],
    } });
    const { fields, append, remove } = useFieldArray({ control, name: 'additions' });
    useEffect(() => {
        const controller = new AbortController();
        let current = true;
        getLookupRecord(definition.id, record.id, controller.signal).then(result => {
            if (current) {
                setDetails(result);
                setValue('recordName', readField(result, 'name') || readField(result, 'code') || '');
            }
        }).catch(reason => { if (current) setError(reason.message || 'Unable to load aliases. Close this window and try again.'); })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; controller.abort(); };
    }, [definition.id, record.id, setValue]);
    const save = handleSubmit(async values => {
        if (busy.current || loading || !details) return;
        busy.current = true; setSaving(true); setError('');
        try {
            const latest = await getLookupRecord(definition.id, record.id);
            setDetails(latest);
            const payload = appendAliasPayload(recordFields, latest, values.additions.map(item => item.value));
            const result = await saveLookupRecord(definition.id, payload, record.id);
            if (result.message) snackbar(result.message, 'success');
            onSuccess();
        } catch (reason) {
            const message = reason.message || 'Unable to add aliases. Please try again.';
            setError(message); snackbar(message, 'error');
        } finally { busy.current = false; setSaving(false); }
    });
    const aliases = splitAliases(readField(details, 'aliases'));
    const deleteAlias = async () => {
        if (busy.current || loading || !details || pendingDelete === null) return;
        busy.current = true; setSaving(true); setError('');
        try {
            const latest = await getLookupRecord(definition.id, record.id);
            setDetails(latest);
            const payload = removeAliasPayload(recordFields, latest, pendingDelete);
            const result = await saveLookupRecord(definition.id, payload, record.id);
            setDetails({ ...latest, aliases: payload.aliases || '' });
            setPendingDelete(null);
            if (result.message) snackbar(result.message, 'success');
            onChanged?.();
        } catch (reason) {
            const message = reason.message || 'Unable to delete this alias. Please try again.';
            setError(message); snackbar(message, 'error');
        } finally { busy.current = false; setSaving(false); }
    };
    const cancelDelete = () => { if (!busy.current) { setPendingDelete(null); setError(''); } };
    return <ConfirmationModal show centered scrollable dialogClassName="modal-md"
        onHide={() => { if (pendingDelete !== null) cancelDelete(); else if (!busy.current) onClose(); }} backdrop="static" keyboard={!saving}
        className="modal-level-10 settings-lookup__record-modal" title={pendingDelete !== null ? 'Delete Alias' : 'Add Aliases'}
        confirmButtonLabel={pendingDelete !== null ? 'Delete Alias' : 'Add Aliases'}
        confirmButtonVariant={pendingDelete !== null ? 'danger' : 'primary'}
        isLoading={loading || saving} onConfirm={pendingDelete !== null ? deleteAlias : save}>
        {error && <p role="alert">{error}</p>}
        {pendingDelete !== null && <div>
            <p>Delete “{pendingDelete}” from “{readField(details, 'name') || readField(details, 'code')}”?</p>
        </div>}
        {loading ? <p role="status">Loading aliases...</p> : details && <form onSubmit={save} hidden={pendingDelete !== null}>
            <FormInput type="text" name="recordName" control={control} label="Name" aria-label="Name" readOnly disabled />
            {aliases.length > 0 && <>
            <p className="settings-lookup__new-alias-heading">Existing aliases</p>
            <div className="settings-lookup__existing-aliases" role="region" aria-label="Existing aliases" tabIndex={0}>
                {aliases.map((alias, index) => <div className="settings-lookup__existing-alias-row" key={index}>
                    <span>{alias}</span>
                    <LoadingButton type="button" variant="link" className="settings-lookup__remove-alias" disabled={saving}
                        aria-label={`Delete alias ${alias}`} onClick={() => { setError(''); setPendingDelete(alias); }}>
                        <DeleteIcon color="var(--ct-danger, #fa5c7c)" aria-hidden="true" />
                    </LoadingButton>
                </div>)}
            </div>
            </>}
            <p className="settings-lookup__new-alias-heading">New aliases</p>
            {fields.map((field, index) => <div key={field.id} className="settings-lookup__new-alias-row">
                <FormInput type="text" name={`additions.${index}.value`} control={control} aria-label={`New alias ${index + 1}`}
                    placeholder="Enter alias" disabled={saving} rules={{ validate: value => !String(value ?? '').trim()
                        ? 'Enter an alias.' : /[;,]/.test(value) ? 'Enter one alias per row without commas or semicolons.' : true }} />
                {fields.length > 1 && <LoadingButton type="button" variant="link" className="settings-lookup__remove-alias" disabled={saving}
                    aria-label={`Remove new alias ${index + 1}`} onClick={() => remove(index)}>
                    <DeleteIcon color="var(--ct-danger, #fa5c7c)" aria-hidden="true" />
                </LoadingButton>}
            </div>)}
            <LoadingButton type="button" variant="outline-primary" disabled={saving} onClick={() => append({ value: '' })}>
                <AddIcon aria-hidden="true" /> Add another
            </LoadingButton>
        </form>}
    </ConfirmationModal>;
};
export default AddAliasesModal;
